import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { Link, useNavigate } from "@tanstack/solid-router";
import { AlertTriangle, BarChart3, CalendarClock, CheckCircle2, Clock, Plus } from "@fleet/icons";
import { cn } from "@tendly/shared";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { ScheduleIllustration } from "@/components/ui/illustrations";
import { PageHeader, Skeleton } from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";
import { useUpcoming } from "../hooks/use-upcoming";
import { UpcomingList } from "./upcoming-list";

/** Urgency summary card (overdue / due soon / on track). */
function UrgencyStat(props: {
  label: string;
  count: number;
  icon: typeof CalendarClock;
  tone: "danger" | "warning" | "success";
}) {
  const tone = () =>
    ({
      danger: "text-danger ring-danger/25 bg-danger/10",
      warning: "text-warning ring-warning/25 bg-warning/10",
      success: "text-success ring-success/20 bg-success/10",
    })[props.tone];

  return (
    <div class="flex items-center gap-3 rounded-2xl border border-border/40 bg-card/40 px-4 py-3">
      <div
        class={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1", tone())}
      >
        <props.icon class="h-5 w-5" />
      </div>
      <div class="min-w-0">
        <div class="font-display text-2xl font-bold leading-none tabular-nums">{props.count}</div>
        <div class="mt-1 truncate text-xs font-medium text-muted-foreground">{props.label}</div>
      </div>
    </div>
  );
}

function UpcomingSkeleton() {
  return (
    <div class="flex flex-col gap-6" data-testid="upcoming-skeleton">
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <For each={[0, 1, 2]}>{() => <Skeleton class="h-[68px] rounded-2xl" />}</For>
      </div>
      <div class="flex flex-col gap-2">
        <For each={[0, 1, 2, 3]}>{() => <Skeleton class="h-[60px] rounded-xl" />}</For>
      </div>
    </div>
  );
}

/** Main screen: what needs attention now and what comes next. */
export function UpcomingView() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const upcoming = useUpcoming();

  const counts = createMemo(() => {
    const c = { overdue: 0, due_soon: 0, ok: 0 };
    for (const item of upcoming.items()) {
      if (item.status === "overdue") {
        c.overdue += 1;
      } else if (item.status === "due_soon") {
        c.due_soon += 1;
      } else if (item.status === "ok") {
        c.ok += 1;
      }
    }
    return c;
  });

  return (
    <div class="flex-1 overflow-y-auto">
      <div class="mx-auto flex w-full max-w-3xl flex-col gap-6 p-4 md:p-8">
        <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <PageHeader
            title={t(msg`Upcoming`)}
            description={
              counts().overdue > 0
                ? t(msg`{count} overdue items need attention`, { count: counts().overdue })
                : t(msg`Maintenance, reminders and warranties at a glance`)
            }
            icon={CalendarClock}
          />
          <div class="flex shrink-0 gap-2">
            <Link to="/reports">
              <Button
                variant="outline"
                class="w-full gap-1.5 sm:w-auto"
                data-testid="go-to-reports"
                aria-label={t(msg`Reports`)}
              >
                <BarChart3 class="h-4 w-4" />
                <span class="hidden sm:inline">{t(msg`Reports`)}</span>
              </Button>
            </Link>
            <Link to="/assets">
              <Button class="w-full gap-1.5 sm:w-auto" data-testid="go-to-assets">
                <Plus class="h-4 w-4" />
                {t(msg`Assets`)}
              </Button>
            </Link>
          </div>
        </div>

        <Show when={!upcoming.isLoading()} fallback={<UpcomingSkeleton />}>
          <Show
            when={upcoming.items().length > 0}
            fallback={
              <EmptyState
                illustration={<ScheduleIllustration />}
                title={t(msg`Nothing scheduled yet`)}
                description={t(
                  msg`Create an asset and add reminders or warranties to see what is due next.`,
                )}
                onIconActivate={() => {
                  void navigate({ to: "/assets" });
                }}
                iconActionLabel={t(msg`Register your first asset`)}
                action={
                  <Link to="/assets">
                    <Button class="gap-1.5" data-testid="empty-go-to-assets">
                      <Plus class="h-4 w-4" />
                      {t(msg`Register your first asset`)}
                    </Button>
                  </Link>
                }
              />
            }
          >
            {/* Urgency summary: at-a-glance overview of general status. */}
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <UrgencyStat
                label={t(msg`Overdue`)}
                count={counts().overdue}
                icon={AlertTriangle}
                tone="danger"
              />
              <UrgencyStat
                label={t(msg`Due soon`)}
                count={counts().due_soon}
                icon={Clock}
                tone="warning"
              />
              <UrgencyStat
                label={t(msg`On track`)}
                count={counts().ok}
                icon={CheckCircle2}
                tone="success"
              />
            </div>

            {/* Single list ordered by urgency (due first). */}
            <UpcomingList items={upcoming.items()} />
          </Show>
        </Show>
      </div>
    </div>
  );
}
